import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  Search, 
  Users, 
  CheckCircle,
  Clock,
  AlertTriangle,
  Plus,
  Minus,
  UserCheck,
  LogOut,
  Smartphone,
  Table2
} from 'lucide-react';

const CheckInInterface = ({ user, onLogout }) => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [buchungen, setBuchungen] = useState([]);
  const [tische, setTische] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [filteredBuchungen, setFilteredBuchungen] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  const [loading, setLoading] = useState(true);
  const [showFreeTables, setShowFreeTables] = useState(false);
  const [newBooking, setNewBooking] = useState({
    typ: 'person',
    vorname: '',
    nachname: '',
    firma: '',
    tickets_erwartet: 1,
    tisch_id: ''
  });
  const letterRefs = useRef({});
  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshIntervalRef = useRef(null);
  const [showBesonderheiten, setShowBesonderheiten] = useState(false);
  const [showSitzplatzSearch, setShowSitzplatzSearch] = useState(false);
  const [sitzplatzForm, setSitzplatzForm] = useState({ block: '', reihe: '', platz: '' });
  const [foundBuchung, setFoundBuchung] = useState(null);

  useEffect(() => {
    if (eventId) {
      fetchData();
    }
  }, [eventId]);

  useEffect(() => {
    filterBuchungen();
  }, [buchungen, searchTerm, statusFilter]);

  // Auto-refresh for real-time sync across devices
  useEffect(() => {
    if (autoRefresh && eventId) {
      // Refresh every 5 seconds
      refreshIntervalRef.current = setInterval(() => {
        fetchData();
      }, 5000);
      
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [autoRefresh, eventId]);

  const fetchData = async () => {
    try {
      console.log('Fetching check-in data for event:', eventId);
      
      // Fetch event and bookings first
      const eventResponse = await axios.get(`/events/${eventId}`);
      const buchungenResponse = await axios.get(`/events/${eventId}/buchungen`);
      
      setEvent(eventResponse.data);
      setBuchungen(buchungenResponse.data);
      
      // Fetch tables and statistics (optional)
      try {
        const tischeResponse = await axios.get(`/events/${eventId}/tische`);
        const statisticsResponse = await axios.get(`/events/${eventId}/statistics`);
        
        // Sort tables numerically by tischnummer
        const sortedTische = tischeResponse.data.sort((a, b) => {
          const numA = parseInt(a.tischnummer) || 0;
          const numB = parseInt(b.tischnummer) || 0;
          if (numA !== numB) return numA - numB;
          // If both are numbers and equal, or if not numbers, sort alphabetically
          return a.tischnummer.localeCompare(b.tischnummer);
        });
        
        setTische(sortedTische);
        setStatistics(statisticsResponse.data);
      } catch (optionalError) {
        console.warn('Could not load tables/statistics:', optionalError);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Check-In-Daten');
      navigate(`/events/${eventId}`);
    } finally {
      setLoading(false);
    }
  };

  const filterBuchungen = () => {
    let filtered = buchungen;
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(buchung => 
        (buchung.vorname && buchung.vorname.toLowerCase().includes(search)) ||
        (buchung.nachname && buchung.nachname.toLowerCase().includes(search)) ||
        (buchung.firma && buchung.firma.toLowerCase().includes(search))
      );
    }
    
    if (statusFilter !== 'alle') {
      filtered = filtered.filter(buchung => buchung.status === statusFilter);
    }
    
    // Separate vollstaendig_da (fully checked in) from others
    const notFullyCheckedIn = filtered.filter(b => b.status !== 'vollstaendig_da');
    const fullyCheckedIn = filtered.filter(b => b.status === 'vollstaendig_da');
    
    // Sort both groups alphabetically
    // Priority: If Firma exists, sort by Firma. Otherwise sort by Nachname
    const sortByName = (a, b) => {
      const nameA = a.firma ? a.firma.toLowerCase() : (a.nachname || '').toLowerCase();
      const nameB = b.firma ? b.firma.toLowerCase() : (b.nachname || '').toLowerCase();
      return nameA.localeCompare(nameB);
    };
    
    notFullyCheckedIn.sort(sortByName);
    fullyCheckedIn.sort(sortByName);
    
    // Combine: not fully checked in first, then fully checked in at the bottom
    setFilteredBuchungen([...notFullyCheckedIn, ...fullyCheckedIn]);
  };

  const updateBuchungStatus = async (buchungId, newTicketsAnwesend) => {
    try {
      const response = await axios.patch(`/buchungen/${buchungId}`, {
        tickets_anwesend: newTicketsAnwesend
      });
      
      // Update local state with response data
      const updatedBuchungen = buchungen.map(buchung => 
        buchung.id === buchungId ? { ...buchung, ...response.data } : buchung
      );
      setBuchungen(updatedBuchungen);
      
      // Refresh statistics
      try {
        const statsResponse = await axios.get(`/events/${eventId}/statistics`);
        setStatistics(statsResponse.data);
      } catch (statsError) {
        console.warn('Could not refresh statistics:', statsError);
      }
      
      const buchung = buchungen.find(b => b.id === buchungId);
      const buchungName = buchung?.vorname 
        ? `${buchung.vorname} ${buchung.nachname}` 
        : buchung?.firma;
      
      toast.success(`${buchungName}: ${newTicketsAnwesend} Tickets anwesend`);
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Fehler beim Check-In');
    }
  };

  const handleSingleGuestCheckIn = (buchung) => {
    const newValue = buchung.tickets_anwesend === 0 ? buchung.tickets_erwartet : 0;
    updateBuchungStatus(buchung.id, newValue);
  };

  const handleContingentIncrement = (buchung) => {
    const newValue = Math.min(buchung.tickets_anwesend + 1, buchung.tickets_erwartet);
    updateBuchungStatus(buchung.id, newValue);
  };

  const handleContingentDecrement = (buchung) => {
    const newValue = Math.max(buchung.tickets_anwesend - 1, 0);
    updateBuchungStatus(buchung.id, newValue);
  };

  const handleAllPresent = (buchung) => {
    updateBuchungStatus(buchung.id, buchung.tickets_erwartet);
  };

  const handleUndo = (buchung) => {
    // Set to one less than expected to make it "teilweise_da" or 0 for "gar_nicht_da"
    const newValue = Math.max(buchung.tickets_erwartet - 1, 0);
    updateBuchungStatus(buchung.id, newValue);
  };

  // Get free tables (tables with available seats)
  const getFreeTables = () => {
    if (!tische || !statistics?.table_statistics) return [];
    
    return tische
      .map(tisch => {
        const stats = statistics.table_statistics.find(s => s.tisch_id === tisch.id);
        // Use ?? instead of || to handle 0 correctly
        const freie = stats?.freie_plaetze ?? tisch.kapazitaet;
        return {
          ...tisch,
          freie_plaetze: freie,
          tickets_erwartet: stats?.tickets_erwartet || 0,
          tickets_anwesend: stats?.tickets_anwesend || 0
        };
      })
      .filter(tisch => tisch.freie_plaetze > 0)  // Only show tables with free seats
      .sort((a, b) => {
        // Sort numerically by tischnummer
        const numA = parseInt(a.tischnummer) || 0;
        const numB = parseInt(b.tischnummer) || 0;
        if (numA !== numB) return numA - numB;
        // Fallback: alphabetically
        return a.tischnummer.localeCompare(b.tischnummer);
      });
  };

  const handleCreateBooking = async () => {
    try {
      if (newBooking.typ === 'person' && (!newBooking.vorname || !newBooking.nachname)) {
        toast.error('Bitte Vor- und Nachname eingeben');
        return;
      }
      
      if (newBooking.typ === 'firma' && !newBooking.firma) {
        toast.error('Bitte Firma eingeben');
        return;
      }
      
      if (!newBooking.tisch_id) {
        toast.error('Bitte Tisch auswählen');
        return;
      }

      const response = await axios.post(`/events/${eventId}/buchungen`, newBooking);
      
      // Add to local state
      setBuchungen([...buchungen, response.data]);
      
      // Refresh data
      fetchData();
      
      // Reset form
      setNewBooking({
        typ: 'person',
        vorname: '',
        nachname: '',
        firma: '',
        tickets_erwartet: 1,
        tisch_id: ''
      });
      
      toast.success('Buchung erfolgreich erstellt');
      setShowFreeTables(false);
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Fehler beim Erstellen der Buchung');
    }
  };

  const handleSitzplatzSearch = async () => {
    if (!sitzplatzForm.block || !sitzplatzForm.reihe || !sitzplatzForm.platz) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    try {
      const response = await axios.get(`/events/${eventId}/buchungen/by-sitzplatz`, {
        params: {
          block: sitzplatzForm.block,
          reihe: sitzplatzForm.reihe,
          platz: sitzplatzForm.platz
        }
      });
      
      setFoundBuchung(response.data);
      toast.success('Buchung gefunden!');
    } catch (error) {
      console.error('Error searching by seat:', error);
      if (error.response?.status === 404) {
        toast.error('Keine Buchung für diesen Sitzplatz gefunden');
        setFoundBuchung(null);
      } else {
        toast.error('Fehler bei der Suche');
      }
    }
  };

  const handleCheckInFoundBuchung = async () => {
    if (!foundBuchung) return;
    
    // Check in all tickets
    await updateBuchungStatus(foundBuchung.id, foundBuchung.tickets_erwartet);
    
    // Close dialog and reset
    setShowSitzplatzSearch(false);
    setSitzplatzForm({ block: '', reihe: '', platz: '' });
    setFoundBuchung(null);
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'gar_nicht_da':
        return (
          <Badge className="bg-gray-100 text-gray-700 flex items-center">
            <Clock className="w-3 h-3 mr-1" />
            Nicht da
          </Badge>
        );
      case 'teilweise_da':
        return (
          <Badge className="bg-amber-100 text-amber-700 flex items-center">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Teilweise
          </Badge>
        );
      case 'vollstaendig_da':
        return (
          <Badge className="bg-green-100 text-green-700 flex items-center">
            <CheckCircle className="w-3 h-3 mr-1" />
            Vollstaendig da
          </Badge>
        );
      default:
        return <Badge className="bg-gray-100 text-gray-700">Unbekannt</Badge>;
    }
  };

  const getDisplayName = (buchung) => {
    if (buchung.nachname && buchung.vorname) {
      return `${buchung.nachname}, ${buchung.vorname}`;
    } else if (buchung.nachname) {
      return buchung.nachname;
    } else if (buchung.vorname) {
      return buchung.vorname;
    }
    return buchung.firma || 'Unbekannt';
  };

  const getTischInfo = (buchung) => {
    if (!buchung.tisch_id || !tische || tische.length === 0) {
      return null;
    }
    
    const tisch = tische.find(t => t.id === buchung.tisch_id);
    if (!tisch) return null;
    
    const tischStats = statistics?.table_statistics?.find(s => s.tisch_id === tisch.id);
    
    return {
      tisch: tisch,
      freie_plaetze: tischStats?.freie_plaetze || 0,
      ueberbucht: tischStats?.freie_plaetze < 0
    };
  };

  // Get available letters from buchungen (excluding fully checked in)
  // Priority: Firma first, then Nachname
  const getAvailableLetters = () => {
    const letters = new Set();
    filteredBuchungen
      .filter(b => b.status !== 'vollstaendig_da') // Exclude fully checked in
      .forEach(buchung => {
        const name = buchung.firma ? buchung.firma.trim() : (buchung.nachname || '').trim();
        if (name) {
          letters.add(name[0].toUpperCase());
        }
      });
    return Array.from(letters).sort();
  };

  // Scroll to specific letter
  const scrollToLetter = (letter) => {
    const element = letterRefs.current[letter];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Get first letter of buchung (prioritize Firma if exists)
  const getFirstLetter = (buchung) => {
    const name = buchung.firma ? buchung.firma.trim() : (buchung.nachname || '').trim();
    return name ? name[0].toUpperCase() : '#';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Check-In Interface...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 dark:from-gray-950 dark:to-black">
      {/* Header */}
      <div className="bg-white shadow-lg border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/events/${eventId}`)}
                className="flex items-center space-x-2"
                data-testid="back-to-event-button"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Event Details</span>
              </Button>
              <div>
                <h1 className="text-lg font-bold font-space-grotesk text-gray-900 flex items-center">
                  <Smartphone className="w-5 h-5 mr-2 text-amber-600" />
                  Check-In Interface
                </h1>
                {event && (
                  <p className="text-xs text-gray-600">{event.name}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Auto-Refresh Indicator */}
              <div 
                className="flex items-center space-x-2 px-3 py-1 bg-green-50 border border-green-200 rounded-md cursor-pointer hover:bg-green-100 transition-all"
                onClick={() => setAutoRefresh(!autoRefresh)}
                title={autoRefresh ? "Auto-Sync aktiv (alle 5 Sek.)" : "Auto-Sync deaktiviert"}
              >
                <div className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                <span className={`text-xs font-medium ${autoRefresh ? 'text-green-700' : 'text-gray-600'}`}>
                  {autoRefresh ? 'Live-Sync' : 'Sync Aus'}
                </span>
              </div>
              
              {/* Besonderheiten Button */}
              {event?.besonderheiten && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBesonderheiten(true)}
                  className="flex items-center space-x-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
                >
                  <AlertTriangle className="w-4 h-4" />
                  <span>Besonderheiten</span>
                </Button>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFreeTables(true)}
                className="flex items-center space-x-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
              >
                <Table2 className="w-4 h-4" />
                <span>Freie Tische</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                className="flex items-center space-x-2 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
              >
                <LogOut className="w-4 h-4" />
                <span>Abmelden</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4 flex">
        {/* Alphabet Navigation */}
        <div className="w-12 flex-shrink-0 mr-4">
          <div className="sticky top-4 bg-white rounded-lg shadow-md p-2 flex flex-col items-center space-y-1">
            {Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ').map(letter => {
              const isAvailable = getAvailableLetters().includes(letter);
              return (
                <button
                  key={letter}
                  onClick={() => isAvailable && scrollToLetter(letter)}
                  disabled={!isAvailable}
                  className={`w-8 h-8 rounded text-xs font-semibold transition-all ${
                    isAvailable
                      ? 'text-amber-600 hover:bg-amber-100 hover:text-amber-700 cursor-pointer'
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                  title={isAvailable ? `Springe zu ${letter}` : `Keine Gäste mit ${letter}`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Search and Filter */}
          <div className="mb-4 space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Nach Name oder Firma suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 text-base py-2 bg-white shadow-sm border-gray-200 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  data-testid="search-guests-input"
                />
              </div>
              <Button
                onClick={() => setShowSitzplatzSearch(true)}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white whitespace-nowrap"
              >
                <Table2 className="w-4 h-4 mr-2" />
                Nach Sitzplatz suchen
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Button
                variant={statusFilter === 'alle' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('alle')}
                className={statusFilter === 'alle' ? 'bg-amber-500 hover:bg-amber-600 text-white text-xs' : 'text-xs'}
              >
                Alle ({buchungen.length})
              </Button>
              <Button
                variant={statusFilter === 'gar_nicht_da' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('gar_nicht_da')}
                className={statusFilter === 'gar_nicht_da' ? 'bg-amber-500 hover:bg-amber-600 text-white text-xs' : 'text-xs'}
              >
                Nicht da ({buchungen.filter(b => b.status === 'gar_nicht_da').length})
              </Button>
              <Button
                variant={statusFilter === 'teilweise_da' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('teilweise_da')}
                className={statusFilter === 'teilweise_da' ? 'bg-amber-500 hover:bg-amber-600 text-white text-xs' : 'text-xs'}
              >
                Teilweise ({buchungen.filter(b => b.status === 'teilweise_da').length})
              </Button>
              <Button
                variant={statusFilter === 'vollstaendig_da' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('vollstaendig_da')}
                className={statusFilter === 'vollstaendig_da' ? 'bg-amber-500 hover:bg-amber-600 text-white text-xs' : 'text-xs'}
              >
                Vollstaendig ({buchungen.filter(b => b.status === 'vollstaendig_da').length})
              </Button>
            </div>
          </div>

          {/* Guest List */}
          {filteredBuchungen.length === 0 ? (
            <Card className="card-glass text-center py-12">
              <CardContent>
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {searchTerm ? 'Keine Treffer gefunden' : 'Keine Gaeste vorhanden'}
                </h3>
                <p className="text-gray-600">
                  {searchTerm 
                    ? `Keine Gaeste entsprechen der Suche "${searchTerm}"`
                    : 'Es sind noch keine Gaeste fuer dieses Event registriert.'
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {/* Active Check-In Section (not fully checked in) */}
              {filteredBuchungen
                .filter(b => b.status !== 'vollstaendig_da')
                .map((buchung, index) => {
                  const tischInfo = getTischInfo(buchung);
                  const currentLetter = getFirstLetter(buchung);
                  const prevBuchung = index > 0 
                    ? filteredBuchungen.filter(b => b.status !== 'vollstaendig_da')[index - 1]
                    : null;
                  const prevLetter = prevBuchung ? getFirstLetter(prevBuchung) : null;
                  const isNewLetter = currentLetter !== prevLetter;
                  
                  return (
                    <div key={buchung.id}>
                      {/* Letter Header */}
                      {isNewLetter && (
                        <div 
                          ref={el => letterRefs.current[currentLetter] = el}
                          className="sticky top-0 z-10 bg-amber-600 text-white px-4 py-1 text-sm font-bold rounded-md mb-2 shadow-md"
                        >
                          {currentLetter}
                        </div>
                      )}
                      
                      <Card 
                        className="card-glass hover:shadow-md transition-all duration-200"
                        data-testid={`guest-card-${index}`}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              {/* Guest Name/Company */}
                              {buchung.firma ? (
                                // If company exists, show company name large, person name small below
                                <div className="mb-1">
                                  <h3 className="text-base font-semibold text-gray-900">
                                    {buchung.firma}
                                  </h3>
                                  {buchung.vorname && buchung.nachname && (
                                    <p className="text-xs text-gray-600">
                                      {buchung.nachname}, {buchung.vorname}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                // If no company, show person name large
                                <h3 className="text-base font-semibold text-gray-900 mb-1">
                                  {buchung.nachname && buchung.vorname 
                                    ? `${buchung.nachname}, ${buchung.vorname}`
                                    : buchung.nachname || buchung.vorname || 'Unbekannt'
                                  }
                                </h3>
                              )}
                              
                              {/* Status and Tickets */}
                              <div className="flex items-center space-x-3 mb-1">
                                {getStatusBadge(buchung.status)}
                                <span className="text-sm text-gray-700 font-medium">
                                  {buchung.tickets_anwesend} / {buchung.tickets_erwartet} Personen
                                </span>
                              </div>
                              
                              {/* Table Info */}
                              {tischInfo && (
                                <div className="flex items-center space-x-3 text-xs text-gray-600">
                                  <div className="flex items-center space-x-1">
                                    <Table2 className="w-3 h-3 text-blue-600" />
                                    <span className="font-medium">Tisch {tischInfo.tisch.tischnummer}</span>
                                  </div>
                                  <Badge 
                                    className={`text-xs ${
                                      tischInfo.ueberbucht 
                                        ? 'bg-red-100 text-red-700' 
                                        : tischInfo.freie_plaetze === 0
                                          ? 'bg-amber-100 text-amber-700'
                                          : 'bg-green-100 text-green-700'
                                    }`}
                                  >
                                    {tischInfo.ueberbucht ? 'Ueberbucht' : `${tischInfo.freie_plaetze} Plaetze frei`}
                                  </Badge>
                                </div>
                              )}
                            </div>
                            
                            {/* Check-in Controls */}
                            <div className="flex items-center space-x-2 ml-4">
                              {buchung.tickets_erwartet === 1 ? (
                                // Single ticket: Only "Alle da" button
                                <Button
                                  onClick={() => handleAllPresent(buchung)}
                                  className={`px-4 py-2 text-xs font-medium rounded-lg transition-all ${
                                    buchung.tickets_anwesend > 0
                                      ? 'bg-green-500 hover:bg-green-600 text-white shadow-lg'
                                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                                  }`}
                                  data-testid={`single-ticket-checkin-${index}`}
                                >
                                  {buchung.tickets_anwesend > 0 ? (
                                    <span className="flex items-center space-x-1">
                                      <CheckCircle className="w-3 h-3" />
                                      <span>Da</span>
                                    </span>
                                  ) : (
                                    'Alle da'
                                  )}
                                </Button>
                              ) : (
                                // Multiple tickets: +/- buttons and "Alle da" button
                                <div className="flex items-center space-x-2">
                                  <Button
                                    onClick={() => handleContingentDecrement(buchung)}
                                    disabled={buchung.tickets_anwesend === 0}
                                    className="w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white disabled:bg-gray-200 disabled:text-gray-400 transition-all p-0"
                                    data-testid={`decrement-button-${index}`}
                                  >
                                    <Minus className="w-4 h-4" />
                                  </Button>
                                  
                                  <div className="w-12 text-center">
                                    <span className="text-2xl font-bold text-gray-900">
                                      {buchung.tickets_anwesend}
                                    </span>
                                  </div>
                                  
                                  <Button
                                    onClick={() => handleContingentIncrement(buchung)}
                                    disabled={buchung.tickets_anwesend >= buchung.tickets_erwartet}
                                    className="w-8 h-8 rounded-full bg-green-500 hover:bg-green-600 text-white disabled:bg-gray-200 disabled:text-gray-400 transition-all p-0"
                                    data-testid={`increment-button-${index}`}
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                  
                                  {buchung.tickets_anwesend < buchung.tickets_erwartet && (
                                    <Button
                                      onClick={() => handleAllPresent(buchung)}
                                      className="ml-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 text-xs font-medium rounded-lg transition-all"
                                      data-testid={`all-present-button-${index}`}
                                    >
                                      Alle da
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              
              {/* Fully Checked In Section (at the bottom) */}
              {filteredBuchungen.filter(b => b.status === 'vollstaendig_da').length > 0 && (
                <div className="mt-8">
                  {/* Section Header */}
                  <div className="bg-green-600 text-white px-4 py-2 text-base font-bold rounded-md mb-3 shadow-md flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Vollstaendig eingecheckt ({filteredBuchungen.filter(b => b.status === 'vollstaendig_da').length})
                  </div>
                  
                  <div className="space-y-2">
                    {filteredBuchungen
                      .filter(b => b.status === 'vollstaendig_da')
                      .map((buchung, index) => {
                        const tischInfo = getTischInfo(buchung);
                        const actualIndex = filteredBuchungen.length - filteredBuchungen.filter(b => b.status === 'vollstaendig_da').length + index;
                        
                        return (
                          <Card 
                            key={buchung.id}
                            className="card-glass opacity-75 hover:opacity-100 transition-all duration-200"
                            data-testid={`guest-card-completed-${index}`}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  {/* Guest Name/Company */}
                                  {buchung.firma ? (
                                    // If company exists, show company name large, person name small below
                                    <div className="mb-1">
                                      <h3 className="text-base font-semibold text-gray-700">
                                        {buchung.firma}
                                      </h3>
                                      {buchung.vorname && buchung.nachname && (
                                        <p className="text-xs text-gray-600">
                                          {buchung.nachname}, {buchung.vorname}
                                        </p>
                                      )}
                                    </div>
                                  ) : (
                                    // If no company, show person name large
                                    <h3 className="text-base font-semibold text-gray-700 mb-1">
                                      {buchung.nachname && buchung.vorname 
                                        ? `${buchung.nachname}, ${buchung.vorname}`
                                        : buchung.nachname || buchung.vorname || 'Unbekannt'
                                      }
                                    </h3>
                                  )}
                                  
                                  {/* Status and Tickets */}
                                  <div className="flex items-center space-x-3 mb-1">
                                    {getStatusBadge(buchung.status)}
                                    <span className="text-sm text-gray-600 font-medium">
                                      {buchung.tickets_anwesend} / {buchung.tickets_erwartet} Personen
                                    </span>
                                  </div>
                                  
                                  {/* Table Info */}
                                  {tischInfo && (
                                    <div className="flex items-center space-x-3 text-xs text-gray-600">
                                      <div className="flex items-center space-x-1">
                                        <Table2 className="w-3 h-3 text-blue-600" />
                                        <span className="font-medium">Tisch {tischInfo.tisch.tischnummer}</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Completed indicator with undo button */}
                                <div className="flex items-center space-x-2 ml-4">
                                  <div className="bg-green-100 text-green-700 px-4 py-2 rounded-lg flex items-center space-x-2">
                                    <CheckCircle className="w-4 h-4" />
                                    <span className="text-sm font-semibold">Vollstaendig</span>
                                  </div>
                                  
                                  <Button
                                    onClick={() => handleUndo(buchung)}
                                    variant="outline"
                                    size="sm"
                                    className="hover:bg-amber-100 hover:border-amber-300 text-amber-700"
                                    title="Rückgängig machen"
                                  >
                                    <Minus className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Free Tables Modal */}
      {showFreeTables && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold flex items-center">
                <Table2 className="w-6 h-6 mr-2" />
                Freie Tische
              </h2>
              <button
                onClick={() => setShowFreeTables(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-all"
              >
                <span className="text-2xl">×</span>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Free Tables List */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Verfügbare Tische ({getFreeTables().length})
                </h3>
                
                {getFreeTables().length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Table2 className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>Keine freien Tische verfügbar</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {getFreeTables().map(tisch => (
                      <Card 
                        key={tisch.id}
                        className={`cursor-pointer transition-all ${
                          newBooking.tisch_id === tisch.id 
                            ? 'border-2 border-blue-500 shadow-lg' 
                            : 'hover:shadow-md'
                        }`}
                        onClick={() => setNewBooking({...newBooking, tisch_id: tisch.id})}
                      >
                        <CardContent className="p-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <h4 className="font-semibold text-lg text-gray-900">
                                Tisch {tisch.tischnummer}
                              </h4>
                              <p className="text-sm text-gray-600">
                                Kapazität: {tisch.kapazitaet} Personen
                              </p>
                            </div>
                            <div className="text-right">
                              <Badge className="bg-green-100 text-green-700 text-sm">
                                {tisch.freie_plaetze} frei
                              </Badge>
                              <p className="text-xs text-gray-500 mt-1">
                                {tisch.tickets_erwartet} gebucht
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* New Booking Form */}
              {newBooking.tisch_id && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Neue Buchung hinzufügen
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Booking Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Buchungstyp
                      </label>
                      <select
                        value={newBooking.typ}
                        onChange={(e) => setNewBooking({
                          ...newBooking, 
                          typ: e.target.value,
                          vorname: '',
                          nachname: '',
                          firma: ''
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="person">Person</option>
                        <option value="firma">Firma</option>
                      </select>
                    </div>

                    {/* Person fields */}
                    {newBooking.typ === 'person' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Vorname *
                          </label>
                          <Input
                            value={newBooking.vorname}
                            onChange={(e) => setNewBooking({...newBooking, vorname: e.target.value})}
                            placeholder="Vorname"
                            required
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nachname *
                          </label>
                          <Input
                            value={newBooking.nachname}
                            onChange={(e) => setNewBooking({...newBooking, nachname: e.target.value})}
                            placeholder="Nachname"
                            required
                            className="w-full"
                          />
                        </div>
                      </div>
                    )}

                    {/* Firma fields */}
                    {newBooking.typ === 'firma' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Firma *
                          </label>
                          <Input
                            value={newBooking.firma}
                            onChange={(e) => setNewBooking({...newBooking, firma: e.target.value})}
                            placeholder="Firmenname"
                            required
                            className="w-full"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Vorname (Optional)
                            </label>
                            <Input
                              value={newBooking.vorname}
                              onChange={(e) => setNewBooking({...newBooking, vorname: e.target.value})}
                              placeholder="Vorname"
                              className="w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Nachname (Optional)
                            </label>
                            <Input
                              value={newBooking.nachname}
                              onChange={(e) => setNewBooking({...newBooking, nachname: e.target.value})}
                              placeholder="Nachname"
                              className="w-full"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {/* Tickets count - for all booking types */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Anzahl Tickets
                      </label>
                      <Input
                        type="number"
                        min="1"
                        value={newBooking.tickets_erwartet}
                        onChange={(e) => setNewBooking({...newBooking, tickets_erwartet: parseInt(e.target.value) || 1})}
                        className="w-full"
                      />
                    </div>

                    {/* Selected Table Info */}
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Ausgewählter Tisch:</span> 
                        {' '}Tisch {tische.find(t => t.id === newBooking.tisch_id)?.tischnummer}
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setNewBooking({
                            typ: 'person',
                            vorname: '',
                            nachname: '',
                            firma: '',
                            tickets_erwartet: 1,
                            tisch_id: ''
                          });
                        }}
                      >
                        Abbrechen
                      </Button>
                      <Button
                        onClick={handleCreateBooking}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Buchung erstellen
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Besonderheiten Dialog */}
      {showBesonderheiten && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold flex items-center">
                <AlertTriangle className="w-6 h-6 mr-2" />
                Besonderheiten für dieses Event
              </h2>
              <button
                onClick={() => setShowBesonderheiten(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-all"
              >
                <span className="text-2xl">×</span>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded">
                <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                  {event?.besonderheiten || 'Keine Besonderheiten für dieses Event.'}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t">
              <Button 
                onClick={() => setShowBesonderheiten(false)}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              >
                Verstanden
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Sitzplatz Search Dialog */}
      {showSitzplatzSearch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold flex items-center">
                <Table2 className="w-6 h-6 mr-2" />
                Nach Sitzplatz suchen
              </h2>
              <button
                onClick={() => {
                  setShowSitzplatzSearch(false);
                  setSitzplatzForm({ block: '', reihe: '', platz: '' });
                  setFoundBuchung(null);
                }}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-all"
              >
                <span className="text-2xl">×</span>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Geben Sie die Sitzplatzinformationen ein, um die zugehörige Buchung zu finden.
              </p>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Block
                  </label>
                  <Input
                    value={sitzplatzForm.block}
                    onChange={(e) => setSitzplatzForm({ ...sitzplatzForm, block: e.target.value })}
                    placeholder="z.B. A, B, C"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reihe
                  </label>
                  <Input
                    value={sitzplatzForm.reihe}
                    onChange={(e) => setSitzplatzForm({ ...sitzplatzForm, reihe: e.target.value })}
                    placeholder="z.B. 5, 12"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Platz
                  </label>
                  <Input
                    value={sitzplatzForm.platz}
                    onChange={(e) => setSitzplatzForm({ ...sitzplatzForm, platz: e.target.value })}
                    placeholder="z.B. 15, 23"
                    className="w-full"
                  />
                </div>
              </div>
              
              <Button
                onClick={handleSitzplatzSearch}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Search className="w-4 h-4 mr-2" />
                Suchen
              </Button>
              
              {/* Search Results */}
              {foundBuchung && (
                <div className="mt-4 border-t pt-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                    Buchung gefunden
                  </h3>
                  
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div>
                      <p className="text-sm text-gray-600">Name/Firma:</p>
                      <p className="font-semibold text-gray-900">
                        {foundBuchung.firma || `${foundBuchung.nachname}, ${foundBuchung.vorname}`}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-sm text-gray-600">Tisch:</p>
                        <p className="font-semibold text-gray-900">
                          {tische.find(t => t.id === foundBuchung.tisch_id)?.tischnummer || 'Nicht zugewiesen'}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600">Tickets:</p>
                        <p className="font-semibold text-gray-900">
                          {foundBuchung.tickets_anwesend} / {foundBuchung.tickets_erwartet}
                        </p>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600">Status:</p>
                      <div className="mt-1">
                        {getStatusBadge(foundBuchung.status)}
                      </div>
                    </div>
                  </div>
                  
                  {foundBuchung.status !== 'vollstaendig_da' && (
                    <Button
                      onClick={handleCheckInFoundBuchung}
                      className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <UserCheck className="w-4 h-4 mr-2" />
                      Jetzt einchecken ({foundBuchung.tickets_erwartet} Tickets)
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckInInterface;